/**
 * AbilityPad — right-thumb ability/attack pad (prototype shell).
 *
 * A fan of action-bar slot buttons around a larger basic-attack button.
 * Every button captures its own pointer (`setPointerCapture`) and forwards
 * press/release to the adapter; the empty space between buttons stays
 * `pointer-events: none` so world clicks (click-to-target) pass through.
 *
 * The pad renders slot numbers only — it does not read ability data, cooldowns
 * or the action bar, and it performs no gameplay action.
 */
import React, { useCallback, useState } from 'react';
import type { TouchControlAdapter } from './types';

export interface AbilityPadProps {
  adapter: TouchControlAdapter;
  /** 1-based action-bar slots to render, in display order. */
  slots?: readonly number[];
  /** Current active page of Bar 1 (defaults to 1). */
  currentPage?: number;
  /** Total pages available on Bar 1 (defaults to 1). */
  totalPages?: number;
  /** Callback to flip Bar 1's page. */
  onPageFlip?: () => void;
  /** Prototype readout hook; receives short human-readable labels. */
  onTelemetry?: (label: string) => void;
  style?: React.CSSProperties;
}

const DEFAULT_SLOTS = [1, 2, 3, 4, 5] as const;
const PAD_WIDTH = 264;
const PAD_HEIGHT = 208;
const ATTACK_SIZE = 84;
const ABILITY_SIZE = 56;
const ARC_RADIUS = 100;
const ARC_START_DEG = 180;
const ARC_SPAN_DEG = 80;

function arcPosition(index: number, count: number): { left: number; top: number } {
  const angleDeg = ARC_START_DEG - (count <= 1 ? ARC_SPAN_DEG / 2 : (index * ARC_SPAN_DEG) / (count - 1));
  const angleRad = (angleDeg * Math.PI) / 180;
  const centerX = PAD_WIDTH - ATTACK_SIZE / 2 - 4;
  const centerY = PAD_HEIGHT - ATTACK_SIZE / 2 - 4;
  return {
    left: centerX + Math.cos(angleRad) * ARC_RADIUS - ABILITY_SIZE / 2,
    top: centerY - Math.sin(angleRad) * ARC_RADIUS - ABILITY_SIZE / 2,
  };
}

function buttonBaseStyle(pressed: boolean, size: number): React.CSSProperties {
  return {
    width: size,
    height: size,
    borderRadius: '50%',
    boxSizing: 'border-box',
    background: pressed ? 'rgba(126, 200, 255, 0.85)' : 'rgba(12, 22, 34, 0.5)',
    border: `2px solid ${pressed ? 'rgba(126, 200, 255, 0.95)' : 'rgba(255, 255, 255, 0.4)'}`,
    color: 'rgba(255, 255, 255, 0.92)',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    fontSize: size > 60 ? 18 : 14,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    pointerEvents: 'auto',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  };
}

export const AbilityPad: React.FC<AbilityPadProps> = ({
  adapter,
  slots = DEFAULT_SLOTS,
  currentPage = 1,
  totalPages = 1,
  onPageFlip,
  onTelemetry,
  style,
}) => {
  const [pressedSlots, setPressedSlots] = useState<ReadonlySet<number>>(new Set());
  const [attackPressed, setAttackPressed] = useState(false);
  const [jumpPressed, setJumpPressed] = useState(false);

  const capture = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch { /* capture unsupported (jsdom / older engines) — handlers still fire */ }
  };

  const setSlotPressed = useCallback((slot: number, pressed: boolean) => {
    setPressedSlots((prev) => {
      const next = new Set(prev);
      if (pressed) next.add(slot);
      else next.delete(slot);
      return next;
    });
  }, []);

  const handleAbilityDown = (slot: number) => (e: React.PointerEvent<HTMLButtonElement>) => {
    capture(e);
    setSlotPressed(slot, true);
    adapter.onAbility(slot, 'press');
    onTelemetry?.(`ability ${slot} press`);
  };

  const handleAbilityUp = (slot: number) => () => {
    setSlotPressed(slot, false);
    adapter.onAbility(slot, 'release');
    onTelemetry?.(`ability ${slot} release`);
  };

  const handleAttackDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    capture(e);
    setAttackPressed(true);
    adapter.onAttack('press');
    onTelemetry?.('attack press');
  };

  const handleAttackUp = () => {
    setAttackPressed(false);
    adapter.onAttack('release');
    onTelemetry?.('attack release');
  };

  const handleJumpDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    capture(e);
    setJumpPressed(true);
    adapter.onJumpPress?.();
    onTelemetry?.('jump press');
  };

  const handleJumpUp = () => {
    setJumpPressed(false);
    onTelemetry?.('jump release');
  };

  return (
    <div
      data-testid="touch-ability-pad"
      data-pressed={Array.from(pressedSlots).sort((a, b) => a - b).join(',')}
      data-attack-pressed={attackPressed ? 'true' : 'false'}
      data-jump-pressed={jumpPressed ? 'true' : 'false'}
      style={{
        position: 'relative',
        width: PAD_WIDTH,
        height: PAD_HEIGHT,
        pointerEvents: 'none',
        ...style,
      }}
    >
      {slots.map((slot, index) => {
        const position = arcPosition(index, slots.length);
        const pressed = pressedSlots.has(slot);
        return (
          <button
            key={slot}
            type="button"
            aria-label={`Ability slot ${slot} (touch prototype, stub adapter)`}
            data-testid={`touch-ability-${slot}`}
            data-slot={slot}
            data-pressed={pressed ? 'true' : 'false'}
            onPointerDown={handleAbilityDown(slot)}
            onPointerUp={handleAbilityUp(slot)}
            onPointerCancel={handleAbilityUp(slot)}
            onLostPointerCapture={handleAbilityUp(slot)}
            style={{ ...buttonBaseStyle(pressed, ABILITY_SIZE), position: 'absolute', ...position }}
          >
            {slot}
            <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.7 }}>SLOT</span>
          </button>
        );
      })}
      <button
        type="button"
        aria-label="Jump"
        data-testid="touch-jump"
        data-pressed={jumpPressed ? 'true' : 'false'}
        onPointerDown={handleJumpDown}
        onPointerUp={handleJumpUp}
        onPointerCancel={handleJumpUp}
        onLostPointerCapture={handleJumpUp}
        style={{
          ...buttonBaseStyle(jumpPressed, 54),
          position: 'absolute',
          right: 16,
          bottom: 96,
          fontSize: 16,
        }}
      >
        ▲
        <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.7 }}>JUMP</span>
      </button>
      <button
        type="button"
        aria-label="Basic attack (touch prototype, stub adapter)"
        data-testid="touch-attack"
        data-pressed={attackPressed ? 'true' : 'false'}
        onPointerDown={handleAttackDown}
        onPointerUp={handleAttackUp}
        onPointerCancel={handleAttackUp}
        onLostPointerCapture={handleAttackUp}
        style={{
          ...buttonBaseStyle(attackPressed, ATTACK_SIZE),
          position: 'absolute',
          right: 4,
          bottom: 4,
          fontSize: 15,
        }}
      >
        ⚔
        <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.7 }}>ATTACK</span>
      </button>
      {onPageFlip && (
        <button
          type="button"
          aria-label={`Action bar page ${currentPage} of ${totalPages} (flip)`}
          data-testid="touch-page-flip"
          onClick={() => {
            onPageFlip();
            onTelemetry?.(`page flip ${currentPage}`);
          }}
          style={{
            ...buttonBaseStyle(false, 42),
            position: 'absolute',
            left: 12,
            bottom: 12,
            fontSize: 12,
          }}
        >
          {currentPage}
          <span style={{ fontSize: 8, fontWeight: 600, opacity: 0.7 }}>PAGE</span>
        </button>
      )}
    </div>
  );
};
